import { BCMSRecord, BCMSRecordProperty } from "@bcm-dev/bcms-vite";
import Classes, { NotificationDetail, User } from "model";


/**
* @class ContactInfo
* @classdesc Class definition for record type from model
* @param {OBJECT} record - record from model to init class
* @extends BCMSRecord
* @example const record = model.contactInfo[0];
* const contactInfo = new ContactInfo(record);
* @property {NUMBER} _id - The id of the contact info
* @property {STRING} _type - The type of the contact info
* @property {STRING} _value - The value of the contact info
* @property {STRING} _isPrimary - The primary value of the contact info
* @property {STRING} _isConfirmed - The confirmed value of the contact info
* @property {STRING} _userEnteredActivationCode - A user entered activation code for a contact info.
 Used in workflows for activating a contact info.
* @property {STRING} _userUpdatedPrimaryStatus - A user updated the "primary" status of a contact info.
This property is not used in the model xml but it used in components for easier workflow implementation.
*/
export default class ContactInfo extends BCMSRecord{
    _id
    _type
    _value
    _isPrimary
    _isConfirmed
    _userEnteredActivationCode
    _userUpdatedPrimaryStatus

    constructor (record){     
      super(record) 
    }

    /**
     * @function getAllUnconfirmedContacts
     * @param {STRING} type - The type of contact info to filter by.
     * @returns {ARRAY}
     * @description Return contact info based on type (phone/email).
     *  If no type is passed, then all unconfirmed contact info is returned.
     * @example 
     * @memberof ContactInfo
     */
    static getAllUnconfirmedContactInfo(type) {
        return ContactInfo.initAll().filter(contactInfo => {
            if(type !== undefined) {
              return contactInfo.isConfirmed === "false"
                && contactInfo.type.toLowerCase() === type.toLowerCase();
            }
            return contactInfo.isConfirmed === "false";
        });
    }

     /**
     * @function getAllContactInfo
     * @param {STRING} type - The type of contact info to filter by.
     * @returns {ARRAY}
     * @description Return contact info based on type (phone/email).
     * @example 
     * @memberof ContactInfo
     */
    static getAllContactInfo(type) {
      return ContactInfo.initAll().filter(contactInfo => {
        return contactInfo.type.toLowerCase() === type.toLowerCase();
      });
    }

    /**
     * @function setNextContactInfoInArrayAsPrimary
     * @param {ContactInfo} contactInfo - The current contact info that the user clicked on.
     * @param {globalController} contactInfo - A global controller in order to modify the global model.
     * @returns {VOID}
     * @description Finds the current primary contact by type
     * (like phone) and sets the next contact as primary instead.
     * This is done in the case of the user deleting a primary contact in a section (like phone),
     * but a primary contact has to exist in that section (like phone).
     * @example 
     * @memberof ContactInfo
     */
    static setNextContactInfoInArrayAsPrimary(contactInfo, globalController) {
      var currentContactInfoByType = ContactInfo.getContactInfoSortedByPrimary(ContactInfo.initAll(), contactInfo.type);

      let user = globalController.state.user;
      // For the selected contact info, change the "isPrimary" value to "true".
      let updatedInfos = currentContactInfoByType.reduce((acc, current, index, arr) => {
        var next = arr[index + 1];
        // If the selected contact info for deletion is "primary",
        // then set the next contact info in the array
        // to "primary". A "primary" contact has to exist.
        // This if statement should only execute 1 time.
        if(current.isPrimary === "true" && contactInfo.id === current.id && next !== undefined) {
          current.isPrimary = "false";
          next.isPrimary = "true";
          next.isConfirmed = "true";
          user[current.type] = next.id;
        }

        acc.push(current.record);
        return acc;
      }, []);

      const updates = NotificationDetail.initAll().map(item => {
        if(item.contactInfoEnabledIDs.includes(contactInfo.id)) {
          const index = item.contactInfoEnabledIDs.indexOf(contactInfo.id);
          item.contactInfoEnabledIDs.splice(index, 1);
        }

        return item.record;
      }, []);

      const detailUpdates = updates.map(item => NotificationDetail.update(item), []);
      const infoUpdates = updatedInfos.map(item => ContactInfo.update(item), []);
      const deleteContact = ContactInfo.delete(contactInfo.id);

      BCMSRecord.batch([
        User.update(user),
        
        ...detailUpdates,
        ...infoUpdates,
        deleteContact
      ], globalController, Classes);
    }

    /**
     * @function getContactInfoSortedByPrimary
     * @param {STRING} type - The current contact info type, either "phone" or "email".
     * @returns {VOID}
     * @description Returns an array of sorted contact info by type (phone/email),
     * with the primary contact info as the first element in the array.
     * @example 
     * @memberof ContactInfo
     */
    static getContactInfoSortedByPrimary(allContactInfo, type) {
      return allContactInfo
      .filter(contact => contact.type === type)
      .sort((a, b) => {
        if(a.isPrimary === "true") {
          return -1;
        } else if(a.isPrimary === "false") {
          return 1;
        } else {
          return 0;
        }
      });
    }

    static getContactInfoIcon(info) {
      switch(info.type) {
        case "email": return "EnvelopeIcon";
        case "phone": return "SquareMessageBubbleIcon";
        case "alert": return "MobilePhoneIcon";

        default: return "EnvelopeIcon";
      }
    }
  }
  ContactInfo.define({
    "recordType":"contactInfo",
    "recordIDProp":"id",
    "className" : "ContactInfo",
    "recordProperties":[
      /** 
       * @name id
       * @instance
       * @property get {NUMBER} - gets the id of the contact info
       * @property set {NUMBER} - sets the id of the contact info
       * @memberof ContactInfo
       */
      BCMSRecordProperty.mapToString("id","_id"),
      /** 
       * @name type
       * @instance
       * @property get {STRING} - gets the type of the contact info
       * @property set {STRING} - sets the type of the contact info
       * @memberof ContactInfo
       */
      BCMSRecordProperty.mapToString("type","_type"),
      /** 
       * @name value
       * @instance
       * @property get {STRING} - gets the value of the contact info
       * @property set {STRING} - sets the value of the contact info
       * @memberof ContactInfo
       */
      BCMSRecordProperty.mapToString("value","_value"),
      /** 
       * @name isPrimary
       * @instance
       * @property get {STRING} - gets the primary value of the contact info
       * @property set {STRING} - sets the primary value of the contact info
       * @memberof ContactInfo
       */
       BCMSRecordProperty.mapToString("isPrimary","_isPrimary"),
       /** 
       * @name isConfirmed
       * @instance
       * @property get {STRING} - gets the confirmed value of the contact info
       * @property set {STRING} - sets the confirmed value of the contact info
       * @memberof ContactInfo
       */
       BCMSRecordProperty.mapToString("isConfirmed","_isConfirmed"),
       /** 
       * @name userEnteredActivationCode
       * @instance
       * @property get {STRING} - gets the value of the contact info
       * @property set {STRING} - sets the value of the contact info
       * @memberof userEnteredActivationCode
       */
      BCMSRecordProperty.mapToString("userEnteredActivationCode","_userEnteredActivationCode"),

      /* @name userUpdatedPrimaryStatus
      * @instance
      * @property get {STRING} - gets the value of the contact info
      * @property set {STRING} - sets the value of the contact info
      * @memberof userUpdatedPrimaryStatus
      */
     BCMSRecordProperty.mapToString("userUpdatedPrimaryStatus","_userUpdatedPrimaryStatus"),
    ]  
})




